/**
 * useAudio - Hook for TTS audio playback
 * 
 * Socket event handling moved to global listeners in socketEventRouter.ts.
 * This hook now only manages:
 * - Autoplay unlock
 * - HTMLAudioElement event listeners
 * - Progress tracking
 * - Visibility state sync
 */

import { useEffect, useRef } from 'react'
import { audioService } from '@/services/audio'
import { useAudioStore } from '@/stores/audioStore'
import { saveTtsProgress, clearTtsProgress, cleanupExpiredTtsProgress } from '@/services/ttsProgress'
import { emitEvent } from '@/services/socket'
import { usePlayerStore } from '@/stores/playerStore'
import { useGameStore } from '@/stores/gameStore'

export function useAudio(): void {
  const progressIntervalRef = useRef<number | null>(null)
  const audioStatus = useAudioStore((state) => state.status)
  const cacheKey = useAudioStore((state) => state.cacheKey)
  const url = useAudioStore((state) => state.url)
  const streamId = useAudioStore((state) => state.streamId)

  // --- Cleanup expired localStorage entries (one-time) ---
  useEffect(() => {
    cleanupExpiredTtsProgress()
  }, [])

  // --- Trigger playback when loading state is set with URL ---
  useEffect(() => {
    if (audioStatus === 'loading' && url && streamId) {
      console.log('[useAudio] Starting playback for stream:', streamId)
      // Small delay to ensure loading state is visible in DOM before playing
      const timer = setTimeout(() => {
        audioService.play(url)
      }, 50)
      return () => clearTimeout(timer)
    }
  }, [audioStatus, url, streamId])

  // --- Reset _playbackFinishedEmitted on loading or resumed (reconnect path) ---
  // gameStore.ts:299-300 calls setLoading + setResumed without audioService.play()
  // React batching may skip the transient 'loading' state, so we must also react to 'resumed'
  useEffect(() => {
    if (audioStatus === 'loading' || audioStatus === 'resumed') {
      audioService.resetPlaybackFinished()
    }
  }, [audioStatus])

  // --- Autoplay unlock (one-time) ---
  useEffect(() => {
    const handler = () => {
      audioService.unlock()
      document.removeEventListener('click', handler)
      document.removeEventListener('touchstart', handler)
    }

    document.addEventListener('click', handler)
    document.addEventListener('touchstart', handler)

    return () => {
      document.removeEventListener('click', handler)
      document.removeEventListener('touchstart', handler)
    }
  }, [])

  // --- HTMLAudioElement event listeners ---
  useEffect(() => {
    const el = audioService.getElement()

    const onPlaying = () => {
      console.log('[useAudio] audio playing')
      useAudioStore.getState().setPlaying()
    }

    const onPause = () => {
      const store = useAudioStore.getState()
      if (
        !audioService.isStopping() &&
        !audioService.isSkipping() &&
        store.status !== 'idle' &&
        store.status !== 'skipped'
      ) {
        console.log('[useAudio] audio paused')
        store.setPaused()
      }
    }

    const onEnded = () => {
      console.log('[useAudio] audio ended')
      if (audioService.isStopping()) return
      if (audioService.playbackFinishedEmitted) return

      const store = useAudioStore.getState()
      const currentStreamId = store.streamId
      if (currentStreamId) {
        store.markFinished(currentStreamId)
      }
      const displayPlayerId = useGameStore.getState().displayPlayerId
      const playerId = usePlayerStore.getState().playerId

      // Emit guard: only display player emits (null-check critical — when null, all emit)
      if (displayPlayerId !== null && playerId !== displayPlayerId) {
        if (store.cacheKey) {
          clearTtsProgress(store.cacheKey)
        }
        store.setStopped()
        return
      }

      if (currentStreamId) {
        emitEvent('tts:playback:finished', { stream_id: currentStreamId })
      }
      audioService.markPlaybackFinished()
      if (store.cacheKey) {
        clearTtsProgress(store.cacheKey)
      }
      store.setStopped()
    }

    const onError = () => {
      console.error('[useAudio] audio error')
      const store = useAudioStore.getState()
      store.setError()
      setTimeout(() => {
        if (useAudioStore.getState().status === 'error') {
          if (store.cacheKey) {
            clearTtsProgress(store.cacheKey)
          }
          useAudioStore.getState().setStopped()
        }
      }, 5000)
    }

    el.addEventListener('playing', onPlaying)
    el.addEventListener('pause', onPause)
    el.addEventListener('ended', onEnded)
    el.addEventListener('error', onError)

    return () => {
      el.removeEventListener('playing', onPlaying)
      el.removeEventListener('pause', onPause)
      el.removeEventListener('ended', onEnded)
      el.removeEventListener('error', onError)
    }
  }, [])

  // --- Progress tracking (save every 2s while playing) ---
  useEffect(() => {
    if (audioStatus === 'playing' && cacheKey) {
      const el = audioService.getElement()

      // Save immediately
      saveTtsProgress(cacheKey, el.currentTime)

      // Then save every 2s
      progressIntervalRef.current = window.setInterval(() => {
        const currentStore = useAudioStore.getState()
        if (currentStore.status === 'playing' && currentStore.cacheKey) {
          saveTtsProgress(currentStore.cacheKey, el.currentTime)
        }
      }, 2000)
    } else {
      // Clear interval when not playing
      if (progressIntervalRef.current !== null) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
    }

    return () => {
      if (progressIntervalRef.current !== null) {
        clearInterval(progressIntervalRef.current)
        progressIntervalRef.current = null
      }
    }
  }, [audioStatus, cacheKey])

  // --- Visibility change handler ---
  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible') {
        const el = audioService.getElement()
        const store = useAudioStore.getState()
        if (el.paused && store.status === 'playing') {
          console.log('[useAudio] Syncing paused state after visibility change')
          store.setPaused()
        }
        if (el.ended && store.status !== 'idle') {
          console.log('[useAudio] Syncing ended state after visibility change')
          if (store.streamId) {
            store.markFinished(store.streamId)
          }
          if (store.cacheKey) {
            clearTtsProgress(store.cacheKey)
          }
          store.setStopped()
        }
      }
    }

    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])
}
